import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { PublicService } from '../services/public.service';
import { PropertySearchMapper } from '../mappers/property-search.mapper';
import { PropertySearchCardVM } from '../models/property-search-card.vm';

/**
 * Estado del home: últimas propiedades. UNA sola fuente compartida por
 * `section-start` (slider) y `section-property` (carrusel) — antes cada
 * sección hacía su propio `GET /api/public/property/latest`.
 *
 * Reusa `PropertySearchMapper` porque `/property/latest` devuelve el mismo
 * `PropertySearchResponseDto` que la búsqueda. Se provee a nivel de
 * `DashboardComponent` para que ambas secciones compartan la instancia.
 */
@Injectable()
export class HomeFacade implements OnDestroy {

    private readonly latest = new BehaviorSubject<PropertySearchCardVM[]>([]);
    private readonly loading = new BehaviorSubject<boolean>(false);
    private loaded = false;
    private readonly subs = new Subscription();

    readonly latest$ = this.latest.asObservable();
    readonly loading$ = this.loading.asObservable();

    constructor(
        private readonly publicService: PublicService,
        private readonly mapper: PropertySearchMapper,
    ) {}

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    /** Carga las últimas propiedades una sola vez (idempotente entre secciones). */
    loadLatest(): void {
        if (this.loaded) {
            return;
        }
        this.loaded = true;
        this.loading.next(true);
        this.subs.add(
            this.publicService.getLatestProperty().pipe(
                map((list: any[]) => (list ?? []).map((dto) => this.mapper.toCardVM(dto))),
                catchError(() => of([] as PropertySearchCardVM[])),
                finalize(() => this.loading.next(false)),
            ).subscribe((cards) => this.latest.next(cards)),
        );
    }
}
